/**
 * Booking-window helpers — client-side enforcement of the advance booking cap.
 *
 * Mirrors the Python rule engine's `_check_booking_window` function in
 * `services/api/src/domain/leave_rules.py`. Keep the two in sync when the
 * policy changes.
 *
 * Leave types with `bookable_in_advance=false` (medical, compassionate) are
 * exempt from the cap: they are filed retrospectively after the event, so
 * there is no sensible upper bound on how far back an employee can file.
 *
 * The feature flag `bookingWindowEnforcement` must be ON for the FE clamp to
 * take effect. The BE enforces independently, so a user who somehow bypasses
 * the FE clamp will still get a 422 from the server.
 */

import { addMonths, endOfYear, format, parseISO } from 'date-fns'

export type BookingWindowReason =
  | 'calendar_year_cap'
  | 'six_month_cap'
  | 'contract_end_cap'

export interface BookingWindow {
  /** Latest date (inclusive) the employee may select as end date. */
  maxDate: string
  /** Which cap is the binding constraint. */
  reason: BookingWindowReason
}

export interface BookingWindowEmployee {
  /**
   * Fixed-term contract end date (ISO, e.g. "2026-10-31"), or null for
   * permanent staff. When set, caps how far ahead leave can be booked.
   */
  contractEndDate?: string | null
  /** HR escape hatch — when true the cap is lifted entirely. */
  bookingWindowOverride?: boolean
}

/**
 * Compute the latest allowed `end_date` for this employee + leave type.
 *
 * Returns `null` when the cap does not apply:
 *   - `enforced` flag is OFF
 *   - leave type is not bookable in advance (retro type)
 *   - employee has an HR override
 */
export function computeBookingWindow(
  today: Date,
  employee: BookingWindowEmployee,
  opts: {
    enforced: boolean
    bookableInAdvance: boolean
  },
): BookingWindow | null {
  if (!opts.enforced) return null
  if (!opts.bookableInAdvance) return null
  if (employee.bookingWindowOverride) return null

  // Base cap: min(today + 6 months, Dec 31 of this year).
  const sixMonthCap = addMonths(today, 6)
  const yearEndCap = endOfYear(today)

  let maxDate: Date
  let reason: BookingWindowReason

  if (sixMonthCap <= yearEndCap) {
    maxDate = sixMonthCap
    reason = 'six_month_cap'
  } else {
    maxDate = yearEndCap
    reason = 'calendar_year_cap'
  }

  // Contract-end tightening: a fixed-term contract caps how far ahead leave
  // can be booked — leave cannot run past the last contracted working day.
  // Probation is deliberately NOT a constraint here.
  if (employee.contractEndDate) {
    const contractCap = parseISO(employee.contractEndDate)
    if (contractCap < maxDate) {
      maxDate = contractCap
      reason = 'contract_end_cap'
    }
  }

  return {
    maxDate: format(maxDate, 'yyyy-MM-dd'),
    reason,
  }
}

/** Human-readable label for a booking window reason. */
export function bookingWindowReasonLabel(reason: BookingWindowReason): string {
  switch (reason) {
    case 'six_month_cap':
      return '6-month advance limit'
    case 'calendar_year_cap':
      return 'end of calendar year'
    case 'contract_end_cap':
      return 'contract end date'
  }
}
